import type { ProfileParams } from '@/api/schema'
import { ERROR_CODES } from '@/api/utils/dict'
import { Row } from '@/app/components/Flex/Flex'
import { Input } from '@/app/components/Input/Input'
import { Layout } from '@/app/components/Layout/Layout'
import { Link } from '@/app/components/Link/Link'
import { Motion } from '@/app/components/Motion/Motion'
import { Terms } from '@/app/components/Terms/Terms'
import { Lib } from '@/app/components/UI/Lib'
import { UI } from '@/app/components/UI/UI'
import { OnboardingType } from '@/app/const'
import { useSession } from '@/app/state/session'
import { fromISODate, toISODate } from '@/app/utils/date'
import { isApiErrorCode, parseError } from '@/app/utils/error'
import { metrikaGoal } from '@/app/utils/metrika'
import type { FunctionalComponent } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { IMaskInput } from 'react-imask'

export const DateView: FunctionalComponent<{
  type: OnboardingType
  userName?: string | null
  birthDate?: string | null
  patchProfile: (params: ProfileParams) => Promise<void> | void
  onSuccess: () => void
  onRateLimit?: (profile: ProfileParams) => void
  onBack: () => void
}> = props => {
  const userId = useSession(s => s.user?.id)

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const [birthDate, setBirthDate] = useState(
    props?.birthDate ? fromISODate(props.birthDate) : '',
  )

  const [userName, setUserName] = useState(props.userName ?? '')

  const dateValid = /^\d{2}\.\d{2}\.\d{4}$/.test(birthDate)

  const valid = dateValid && !!userName

  useEffect(() => {
    metrikaGoal('onboard_date')
  }, [])

  return (
    <Layout.Content
      help={props.type !== OnboardingType.MatrixMoney
        && props.type !== OnboardingType.MatrixLove
        && props.type !== OnboardingType.MatrixKarma}
      onBack={props.type !== OnboardingType.MatrixMoney
          && props.type !== OnboardingType.MatrixLove
          && props.type !== OnboardingType.MatrixKarma
        ? props.onBack
        : undefined}
      key={`date-view-${props.type}`}
    >
      <form
        onSubmit={async e => {
          e.preventDefault()

          try {
            setLoading(true)
            await props.patchProfile({
              userName,
              birthDate: toISODate(birthDate),
            })
            metrikaGoal('login_submit_bday')
            props.onSuccess()
          }
          catch (e) {
            console.error(e)

            if (isApiErrorCode(e, ERROR_CODES.tooManyRequests) && props.onRateLimit) {
              props.onRateLimit({
                userName,
                birthDate: toISODate(birthDate),
              })
              return
            }

            setErrors(parseError(e))
          }
          finally {
            setLoading(false)
          }
        }}
      >
        <Motion>
          <Row gap={15}>
            <UI.Avatar
              label
              size={64}
              bot={props.type}
            />

            <Lib.Fields>
              <Lib.SystemMessage>
                {props.type === OnboardingType.Astro
                  ? <>Привет! Я Зоя. Заполни профиль для построения натальной карты.</>
                  : props.type === OnboardingType.Daily
                  ? <>Привет! Я Зоя. Заполни профиль для персонального дейлика.</>
                  : props.type === OnboardingType.Matrix
                  ? <>Привет! Я Милана. Заполни профиль для расчёта матрицы судьбы.</>
                  : props.type === OnboardingType.MatrixMoney
                  ? (
                    <>
                      Привет! Я Милана. Рассчитаю <b>твой денежный канал</b> и покажу его сильные стороны и точки роста.
                    </>
                  )
                  : props.type === OnboardingType.MatrixLove
                  ? (
                    <>
                      Привет! Я Милана. Рассчитаю <b>твою линию любви</b> и расскажу, как ты проявляешься в любви и что влияет на отношения.
                    </>
                  )
                  : props.type === OnboardingType.MatrixKarma
                  ? (
                    <>
                      Привет! Я Милана. Рассчитаю <b>твой кармический хвост</b>{' '}
                      и покажу, какие задачи он несёт и как они могут проявляться в жизни.
                    </>
                  )
                  : props.type === OnboardingType.Tarot
                  ? <>Привет! Я Ева. Заполни профиль для расклада Таро.</>
                  : props.type === OnboardingType.Synastry
                  ? <>Привет! Я Зоя. Заполни профиль для расчёта совместимости.</>
                  : <>Привет! Я Зоя. Заполни профиль, для персональных разборов.</>}

                {props.type !== OnboardingType.MatrixMoney
                  && props.type !== OnboardingType.MatrixLove
                  && props.type !== OnboardingType.MatrixKarma
                  && !userId
                  && (
                    <>
                      <br />
                      <br />
                      Уже есть аккаунт?{' '}
                      <Link to='~/login'>
                        Войти
                      </Link>
                    </>
                  )}
              </Lib.SystemMessage>

              <Input.Block label='Твоё имя'>
                <Input
                  placeholder='Введи имя'
                  value={userName}
                  onChange={setUserName}
                  onInput={setUserName}
                  disabled={loading}
                />
              </Input.Block>

              <Input.Block label='Дата рождения'>
                <Input
                  type='tel'
                  placeholder='DD.MM.YYYY'
                  value={birthDate}
                  onInput={v => {
                    setBirthDate(v)
                  }}
                  onChange={v => {
                    setBirthDate(v)
                  }}
                  disabled={loading}
                >
                  {props => (
                    <IMaskInput
                      {...props}
                      overwrite
                      type='tel'
                      mask={Date}
                      min={new Date(1900, 0, 1)}
                      max={new Date(2050, 0, 1)}
                    />
                  )}
                </Input>
              </Input.Block>
            </Lib.Fields>

            <Lib.Errors errors={errors} />

            <Row gap={15}>
              <UI.Button
                size='l'
                type='primary'
                htmlType='submit'
                disabled={!valid || loading}
              >
                {props.type === OnboardingType.MatrixMoney
                    || props.type === OnboardingType.MatrixKarma
                    || props.type === OnboardingType.MatrixLove
                  ? 'Рассчитать'
                  : 'Продолжить'}
              </UI.Button>

              <UI.Hint>
                <Terms
                  btn={props.type === OnboardingType.MatrixMoney
                      || props.type === OnboardingType.MatrixKarma
                      || props.type === OnboardingType.MatrixLove
                    ? 'Рассчитать'
                    : 'Продолжить'}
                />
              </UI.Hint>
            </Row>
          </Row>
        </Motion>
      </form>
    </Layout.Content>
  )
}
